import React, { useState } from "react";
import {
  StyleSheet,
  Pressable,
  ViewStyle,
  TextStyle,
  View,
  Platform,
  StyleProp,
  DimensionValue,
} from "react-native";
import variables from "../../styles/variables";
import Icon from "../Icon";
import RText from "../RText";
import { useResponsive } from "../../hooks/useResponsive";

/**
 * A customizable button component with various styles and behaviors.
 * @component
 * @param {Object} props - The properties object.
 * @param {string} [props.title] - The text to display on the button.
 * @param {"primary" | "secondary" | "tertiary" | "disable"} [props.color="primary"] - The color theme of the button.
 * @param {"hover" | "selected" | "default"} [props.initialBehaviour="default"] - The initial behavior state of the button.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {"default" | "hover" | "selected"} [props.behaviour] - The current behavior state of the button.
 * @param {React.ReactNode} [props.children] - Additional elements to display inside the button.
 * @param {ViewStyle} [props.style] - Custom styles for the button container.
 * @param {ViewStyle} [props.borderStyle] - Custom styles for the button border.
 * @param {Function} [props.onPress] - Function to call when the button is pressed.
 * @param {StyleProp<TextStyle>} [props.customTextStyle] - Custom styles for the button text.
 * @param {boolean} [props.locked=false] - Whether to display a lock icon on the button.
 * @param {boolean} [props.wide=false] - Whether the button should take full width of its container.
 * @param {string} [props.accessibilityLabel] - Accessibility label for the button.
 * @param {object} props.dataSetButton - Data set attributes for the button (for web) - this is a selector that RTV can use to target the button styles on GrowthBook for a/b experimenting.
 * @param {object} props.dataSetButtonText - Data set attributes for the button text (for web) - as above, a selector for GrowthBook but to target the button text.
 *
 * @example
 * <RButton
 *   title="Click Me"
 *   color="primary"
 *   onPress={() => console.log("Button Pressed")}
 *  />
 *
 * @returns {JSX.Element} The rendered button component.
 *
 */

export type RButtonProps = {
  title?: string;
  color?: "primary" | "secondary" | "tertiary" | "disable";
  initialBehaviour?: "hover" | "selected" | "default";
  disabled?: boolean;
  behaviour?: "default" | "hover" | "selected";
  children?: React.ReactNode;
  style?: ViewStyle;
  borderStyle?: ViewStyle;
  onPress?: Function;
  customTextStyle?: StyleProp<TextStyle>;
  locked?: boolean;
  wide?: boolean;
  accessibilityLabel?: string;
  dataSetButton?: object;
  dataSetButtonText?: object;
  backgroundColorOverride?: string;
  textColorOverride?: string;
  fontSizeOverride?: number;
  boldText?: boolean;
  alignItems?: ViewStyle["alignItems"];
  minWidth?: DimensionValue;
};

export default function RButton(props: RButtonProps) {
  const {
    title,
    onPress = () => console.log(`pressed ${title}`),
    color = "primary",
    initialBehaviour = "default",
    disabled = false,
    children,
    style,
    borderStyle,
    customTextStyle,
    locked = false,
    wide = false,
    accessibilityLabel = undefined,
    dataSetButton = {},
    dataSetButtonText = {},
    backgroundColorOverride,
    textColorOverride,
    fontSizeOverride,
    boldText = true,
    alignItems = "center",
    minWidth = undefined,
  } = props;

  let activeColor = disabled ? "disable" : color;

  const { isSmallAndDown } = useResponsive();

  const [behaviour, setBehaviour] = useState(initialBehaviour);

  const handleOnPress = () => {
    if (disabled) return;
    setBehaviour(initialBehaviour);
    onPress();
  };

  return (
    <View
      style={[{ alignItems: alignItems, minWidth: minWidth }]}
      {...(Platform.OS === "web" ? { dataSet: dataSetButton } : {})}
    >
      <Pressable
        accessibilityLabel={accessibilityLabel}
        style={[
          styles.button,
          colorStyles[activeColor][behaviour],

          backgroundColorOverride
            ? {
                backgroundColor: backgroundColorOverride,
                borderColor: backgroundColorOverride,
              }
            : {},
          locked ? { opacity: 0.3 } : null,
          wide ? { minWidth: "100%" } : null,

          Platform.OS === "web"
            ? ({
                cursor: (disabled
                  ? "not-allowed"
                  : "pointer") as ViewStyle["cursor"],
              } as ViewStyle)
            : {},

          { ...style },
          borderStyle,
        ]}
        onHoverIn={() => setBehaviour("hover")}
        onHoverOut={() => setBehaviour(initialBehaviour)}
        onPressIn={() => setBehaviour("selected")}
        onPress={() => {
          if (disabled) return;
          setBehaviour(initialBehaviour);
          onPress();
        }}
      >
        <RText
          style={[
            {
              textAlign: "center",
              fontSize: fontSizeOverride ? fontSizeOverride : 14,
            } as TextStyle,
            textColorOverride
              ? { color: textColorOverride }
              : textStyle[activeColor],
            behaviour === "selected" ? textStyle.whiteSelected : null,
            customTextStyle,
          ]}
          dataSet={dataSetButtonText}
          weight={boldText ? "bold" : "regular"}
        >
          {title}
        </RText>
        {children}
      </Pressable>
      <Pressable
        style={{ alignItems: "center", position: "absolute" }}
        onPress={handleOnPress}
      >
        {locked ? (
          <Icon
            name="lock"
            size="large"
            style={{ position: "absolute", opacity: 1 }}
          />
        ) : null}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  button: {
    justifyContent: "center",
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 36,
    borderWidth: 1.5,
    userSelect: "none",
  },
});

const {
  default: defaultStyle,
  outline,
  alternate,
  disabled,
} = variables.colors.palette.buttons;

// color themes for different behaviours - they are used in eval function
const primary = StyleSheet.create({
  default: {
    backgroundColor: defaultStyle.backgroundColor,
    borderColor: defaultStyle.borderColor,
  },
  hover: {
    backgroundColor: defaultStyle.hovered.backgroundColor,
    borderColor: defaultStyle.hovered.borderColor,
    // ...Platform.select({
    //   web: {
    //     cursor: "pointer",
    //   },
    // }),
  },
  selected: {
    backgroundColor: defaultStyle.selected.backgroundColor,
    borderColor: defaultStyle.selected.borderColor,
  },
});

const secondary = StyleSheet.create({
  default: {
    backgroundColor: outline.backgroundColor,
    borderColor: outline.borderColor,
  },
  hover: {
    backgroundColor: outline.hovered.backgroundColor,
    borderColor: outline.hovered.borderColor,
    // ...Platform.select({
    //   web: {
    //     cursor: "pointer",
    //   },
    // }),
  },
  selected: {
    backgroundColor: outline.selected.backgroundColor,
    borderColor: outline.selected.borderColor,
  },
});

const tertiary = StyleSheet.create({
  default: {
    backgroundColor: alternate.backgroundColor,
    borderColor: alternate.borderColor,
  },
  hover: {
    backgroundColor: alternate.hovered.backgroundColor,
    borderColor: alternate.hovered.borderColor,
    // ...Platform.select({
    //   web: {
    //     cursor: "pointer",
    //   },
    // }),
  },
  selected: {
    backgroundColor: alternate.selected.backgroundColor,
    borderColor: alternate.selected.borderColor,
  },
});

const disable = StyleSheet.create({
  default: {
    backgroundColor: disabled.backgroundColor,
    borderColor: disabled.borderColor,
    // pointerEvents: "none",
    // cursor: "not-allowed",
  },
  hover: {
    backgroundColor: disabled.backgroundColor,
    borderColor: disabled.borderColor,
    // cursor: "not-allowed",
  },
  selected: {
    backgroundColor: disabled.backgroundColor,
    borderColor: disabled.borderColor,
    // cursor: "not-allowed",
  },
});

const textStyle = StyleSheet.create({
  primary: {
    color: defaultStyle.color,
  },
  secondary: {
    color: outline.color,
  },
  tertiary: {
    color: alternate.color,
  },
  disable: {
    color: disabled.text.color,
  },
  whiteSelected: {
    color: defaultStyle.color,
  },
});

// Map styles to color names
const colorStyles = {
  primary,
  secondary,
  tertiary,
  disable,
};
